/**
 * AI 任务计数与列表：角标独立刷新，完整历史仅在弹层打开时拉取。
 * GET /api/v1/ai/jobs
 */
import { useCallback } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { getErrorCode } from '@/lib/error-utils';
import { aiApi } from '@/services/api';
import { useAppStore } from '@/stores/appStore';
import { useOrgContextStore } from '@/stores/orgContextStore';
import { useUIStore } from '@/stores/uiStore';
import type { AIJobsListResponse } from '@/types/ai';

export const aiJobsKeys = {
  all: ['ai-jobs'] as const,
  scope: (userId: string | null, activeOrgId: string | null) =>
    [...aiJobsKeys.all, userId, activeOrgId ?? '__personal__'] as const,
  list: (userId: string | null, activeOrgId: string | null, params?: Record<string, unknown>) =>
    [...aiJobsKeys.scope(userId, activeOrgId), 'list', params] as const,
  activeCount: (userId: string | null, activeOrgId: string | null, projectId?: string | null) =>
    [...aiJobsKeys.scope(userId, activeOrgId), 'active-count', projectId] as const,
};

const PAGE_SIZE = 30;

export function useAIActiveJobCount(projectId?: string | null) {
  const userId = useAppStore((state) => state.user?.userId ?? null);
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  const isSwitching = useOrgContextStore((state) => state.isSwitching);

  return useQuery({
    queryKey: aiJobsKeys.activeCount(userId, activeOrgId, projectId),
    queryFn: async () => {
      const result = await aiApi.getJobs(
        { projectId: projectId!, activeOnly: true, page: 1, pageSize: 1 },
        { activeOrgId }
      );
      return result.total;
    },
    enabled: Boolean(userId && projectId) && !isSwitching,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    // 永久性错误停止轮询，即使缓存仍有任务；网络、超时、限流和服务端错误允许恢复。
    refetchInterval: (query) => {
      if (query.state.status === 'error') {
        const code = getErrorCode(query.state.error);
        if (code !== undefined && code >= 400 && code < 500 && code !== 408 && code !== 429) {
          return false;
        }
        return 10_000;
      }
      return (query.state.data ?? 0) > 0 ? 10_000 : false;
    },
    refetchIntervalInBackground: false,
  });
}

/** API 提交成功后统一调用；无任务不通知，批量任务只触发一次刷新和动画。 */
export function useNotifyAIJobSubmit() {
  const queryClient = useQueryClient();
  return useCallback(
    (jobIds: string | null | undefined | readonly (string | null | undefined)[]) => {
      const ids = Array.isArray(jobIds) ? jobIds : [jobIds];
      if (!ids.some((id) => typeof id === 'string' && id.trim())) return;
      // 首次查询还未返回时，单纯 invalidate 会复用提交前的请求，可能把角标留在 0。
      void queryClient
        .cancelQueries({
          queryKey: aiJobsKeys.all,
          predicate: (query) => query.queryKey[3] === 'active-count',
        })
        .then(() =>
          queryClient.invalidateQueries({
            queryKey: aiJobsKeys.all,
            predicate: (query) => query.queryKey[3] === 'active-count',
          })
        );
      // 不打断列表分页；已有请求落地后再刷新，避免下一页响应覆盖提交后的失效标记。
      const lists = queryClient.getQueryCache().findAll({
        queryKey: aiJobsKeys.all,
        predicate: (query) => query.queryKey[3] === 'list',
      });
      for (const query of lists) {
        const refresh = () =>
          queryClient.invalidateQueries(
            { queryKey: query.queryKey, exact: true },
            { cancelRefetch: false }
          );
        if (query.state.fetchStatus !== 'idle' && query.promise) {
          void query.promise.then(refresh, refresh);
        } else {
          void refresh();
        }
      }
      useUIStore.getState().notifyAIJobSubmit();
    },
    [queryClient]
  );
}

export function useAIJobs(params?: { projectId?: string; tabType?: number; enabled?: boolean }) {
  const userId = useAppStore((state) => state.user?.userId ?? null);
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  const isSwitching = useOrgContextStore((state) => state.isSwitching);
  const queryParams = {
    projectId: params?.projectId,
    tabType: params?.tabType,
  };

  const query = useInfiniteQuery<
    AIJobsListResponse,
    Error,
    { pages: AIJobsListResponse[] },
    ReturnType<typeof aiJobsKeys.list>,
    number
  >({
    queryKey: aiJobsKeys.list(userId, activeOrgId, queryParams),
    queryFn: ({ pageParam }) =>
      aiApi.getJobs(
        {
          projectId: params?.projectId,
          tabType: params?.tabType,
          page: pageParam,
          pageSize: PAGE_SIZE,
        },
        { activeOrgId }
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
    staleTime: 0,
    placeholderData: (previousData, previousQuery) => {
      const key = previousQuery?.queryKey;
      // 只保留同一作用域下切换类型时的列表，不能把旧账号/组织/项目的数据带过去。
      return key?.[1] === userId &&
        key[2] === (activeOrgId ?? '__personal__') &&
        key[4]?.projectId === params?.projectId
        ? previousData
        : undefined;
    },
    enabled: Boolean(userId && params?.projectId) && !isSwitching && (params?.enabled ?? false),
  });

  const allItems = query.data?.pages.flatMap((p) => p.items) ?? [];
  const total = query.data?.pages[query.data.pages.length - 1]?.total ?? 0;

  return { ...query, allItems, total };
}
